import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TONE } from '../theme/adminVisualSystem';

/**
 * IdeaScoreBadge — gamified circular gauge for idea_score.
 * Colors:
 *   ≥6   gold (top tier)
 *   ≥4   green (high)
 *   ≥2   cyan (medium)
 *   ≥0   amber (low)
 *   <0   red  (negative)
 *   null muted  (unscored)
 *
 * Score range, given default weights and 1..10 inputs:
 *   theoretical max = 1·0.3 + 1·0.5 - 1·0.2 → ~ -2 .. +8
 */
function scoreTone(score) {
  if (score == null || Number.isNaN(score)) return TONE.muted;
  if (score >= 6) return TONE.gold;
  if (score >= 4) return TONE.green;
  if (score >= 2) return TONE.cyan;
  if (score >= 0) return TONE.amber;
  return TONE.red;
}

export function IdeaScoreBadge({ score, size = 56, label = true, testId }) {
  const tone = scoreTone(score);
  const display = score == null ? '—' : Number(score).toFixed(2);
  // Map score 0..8 → 0..100% for the ring fill (clamped)
  const pct = Math.max(0, Math.min(100, score == null ? 0 : (score / 8) * 100));

  const radius = (size - 4) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - pct / 100);

  return (
    <div
      className="flex items-center gap-3 shrink-0"
      data-testid={testId || 'idea-score-badge'}
      title={`Idea Score = (H·Wh) + (G·Wg) − (C·Wc)`}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} stroke="#1a1a1a" strokeWidth="3" fill="none" />
          {score != null && (
            <motion.circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={tone.text}
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
              initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
              animate={{ strokeDasharray: circumference, strokeDashoffset: offset }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              style={{ filter: `drop-shadow(0 0 6px ${tone.glow})` }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="font-outfit text-[12px] font-semibold tabular-nums leading-none"
            style={{ color: tone.text }}
          >
            {display}
          </span>
        </div>
      </div>
      {label && (
        <div className="leading-tight">
          <p className="text-[9px] font-mono text-[#666] tracking-[0.18em] uppercase">Idea Score</p>
          <p
            className="font-outfit text-base font-semibold tabular-nums"
            style={{ color: tone.text }}
          >
            {display}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * MiniMetricBar — horizontal bar for compact view.
 * 1..10 input mapped to 10..100% width.
 */
export function MiniMetricBar({ label, value, accent }) {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, (Number(value) / 10) * 100));
  return (
    <div className="space-y-1 min-w-0">
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-mono text-[#666] tracking-[0.16em] uppercase">{label}</span>
        <span className="text-[10px] font-mono tabular-nums" style={{ color: value == null ? '#444' : '#ccc' }}>
          {value == null ? '—' : `${value}/10`}
        </span>
      </div>
      <div className="h-1 bg-[#121212] overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="h-full"
          style={{
            background: accent,
            boxShadow: pct > 0 ? `0 0 8px ${accent}88` : 'none',
          }}
        />
      </div>
    </div>
  );
}

/**
 * MiniMetersRow — Hustle / Gains / Cost mini bars side-by-side.
 */
export function MiniMetersRow({ hustle, gains, cost }) {
  const allEmpty = hustle == null && gains == null && cost == null;
  if (allEmpty) {
    return (
      <p className="text-[10px] font-mono text-[#444] uppercase tracking-[0.18em]">
        Not scored yet
      </p>
    );
  }
  return (
    <div className="grid grid-cols-3 gap-3">
      <MiniMetricBar label="Hustle" value={hustle} accent={TONE.amber.text} />
      <MiniMetricBar label="Gains" value={gains} accent={TONE.green.text} />
      <MiniMetricBar label="Cost" value={cost} accent={TONE.red.text} />
    </div>
  );
}

/**
 * MetricRing — semi-circular meter for the expanded view.
 */
export function MetricRing({ label, value, accent, max = 10 }) {
  const size = 84;
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = value == null ? 0 : Math.max(0, Math.min(100, (Number(value) / max) * 100));
  const offset = circumference * (1 - pct / 100);

  const [animated, setAnimated] = useState(0);
  useEffect(() => {
    if (value == null) {
      setAnimated(0);
      return;
    }
    let raf;
    const start = performance.now();
    const dur = 700;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setAnimated(value * p);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} stroke="#1a1a1a" strokeWidth={stroke} fill="none" />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={accent}
            strokeWidth={stroke}
            strokeLinecap="round"
            fill="none"
            initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            style={{ filter: pct > 0 ? `drop-shadow(0 0 6px ${accent}99)` : 'none' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-outfit text-lg font-semibold text-white tabular-nums leading-none">
            {value == null ? '—' : Math.round(animated)}
          </span>
          <span className="text-[8px] font-mono text-[#555] tracking-wider mt-0.5">
            / {max}
          </span>
        </div>
      </div>
      <p className="text-[10px] font-mono text-[#888] tracking-[0.16em] uppercase">{label}</p>
    </div>
  );
}

export { scoreTone };
